import DataGrid, { Column, DataGridRef } from "devextreme-react/data-grid";
import { cellRenderer, customHeader } from "../SAR/SARGrid";
import { formatDate, formatNumber } from "devextreme/localization";
import { useLTIPTranch } from "@/hooks/useLTIPTranch";
import { GridSkeleton } from "@/components/Skeletons";
import { useRef } from "react";
import { createPortal } from "react-dom";
import KeyValueSection from "@/components/KeyValueSection";
import {
  calculateAwardsVestedToDateSum,
  calculateAwardsUnvestedToDateSum,
  calculateTotalValueSum,
  calculateAwardsVestedToDate,
  calculateAwardsUnvestedToDate,
  calculateTotalValue,
  onRowPreparedNeedToDiscussWithClient,
} from "../RSU/rsu-psu-utils";
import { FAWithPrice } from "@/types/types";

type Props = {
  psu: Partial<FAWithPrice> | undefined;
  stockPrice: number;
};

function Grid({ psu, stockPrice }: Props) {
  const { data, isLoading, isError, isRefetching } = useLTIPTranch(psu?.externalCode);
  const dxGridRef = useRef<DataGridRef>(null);

  if (isLoading || isRefetching) return <GridSkeleton columns={7} rows={4} className="pt-4" />;
  if (isError) return <div>Failed fetching PSU tranches</div>;

  const psuSummedValues = [
    {
      header: "Awards Vested To Date",
      value: calculateAwardsVestedToDateSum(data?.data ?? []),
      isCurrency: false,
    },
    {
      header: "Awards Unvested To Date",
      value: calculateAwardsUnvestedToDateSum(data?.data ?? []),
      isCurrency: false,
    },
    {
      header: "Total Value",
      value: formatNumber(calculateTotalValueSum(data?.data ?? [], stockPrice), "currency"),
    },
  ];

  const psuCombinedElement = document.getElementById("psu-combined-values");

  const onContentReady = () => {
    const gridInstance = dxGridRef?.current?.instance;
    if (!gridInstance) return;

    const element = gridInstance().element().querySelector(".dx-sort") as HTMLElement;

    if (element) {
      element.style.marginTop = "80%";
    }
  };

  return (
    <>
      {createPortal(
        <KeyValueSection
          items={psuSummedValues}
          sectionHeading={`${psu?.ticker ? `${psu?.ticker} Total` : "No Ticker!"}`}
        />,
        psuCombinedElement!
      )}
      <DataGrid
        id={`psu-grid-${psu?.externalCode}`}
        dataSource={data?.data}
        showBorders={true}
        showRowLines={true}
        onContentReady={onContentReady}
        showColumnLines={true}
        ref={dxGridRef}
        className="ao-grid"
        wordWrapEnabled={true}
        onRowPrepared={onRowPreparedNeedToDiscussWithClient}
        onCellPrepared={(e) => {
          const style = e.cellElement.style;
          const rowData = e.row?.data;
          if (e.rowType === "header") {
            style.backgroundColor = "var(--primary-color)";
            style.color = "white";
          } else if (!calculateAwardsUnvestedToDate(rowData)) {
            style.backgroundColor = "#F2F2F2";
            style.fontStyle = "italic";
          }
        }}
      >
        <Column dataField={"id"} visible={false} />
        <Column
          dataField="restrictedShares"
          allowSorting={false}
          allowEditing={false}
          alignment={"center"}
          minWidth={100}
          headerCellRender={() =>
            customHeader(`${psu?.externalCode}-restricted-shares`, "Restricted", "Shares")
          }
          cellRender={(cellData) => {
            return cellData.data.options
              ? cellRenderer(
                  cellData,
                  "options-granted",
                  formatNumber(cellData?.data?.options, "#,##0")
                )
              : "";
          }}
        />
        <Column
          dataField="grantDate"
          dataType="date"
          allowSorting={false}
          sortOrder={"asc"}
          allowEditing={false}
          minWidth={130}
          alignment={"center"}
          headerCellRender={() => customHeader(`${psu?.externalCode}-options`, "Award", "Date")}
          cellRender={(cellData) => {
            return cellData.data.grantDate
              ? cellRenderer(
                  cellData,
                  "date-of-grant",
                  formatDate(new Date(cellData?.data?.grantDate), "MMM dd, yyyy")
                )
              : "";
          }}
        />
        <Column
          dataField="awardsVested"
          allowSorting={false}
          allowEditing={false}
          minWidth={100}
          alignment={"center"}
          headerCellRender={() =>
            customHeader(`${psu?.externalCode}-options-vested`, "Awards Vested", "To Date")
          }
          cellRender={(cellData) => {
            return calculateAwardsVestedToDate(cellData?.data)
              ? cellRenderer(
                  cellData,
                  "options-vested",
                  formatNumber(calculateAwardsVestedToDate(cellData?.data), "#,##0")
                )
              : "";
          }}
        />
        <Column
          dataField="awardsUnvested"
          allowSorting={false}
          allowEditing={false}
          minWidth={100}
          alignment={"center"}
          headerCellRender={() =>
            customHeader(`${psu?.externalCode}-options-vested`, "Awards Unvested", "To Date")
          }
          cellRender={(cellData) => {
            return calculateAwardsUnvestedToDate(cellData?.data)
              ? cellRenderer(
                  cellData,
                  "options-vested",
                  formatNumber(calculateAwardsUnvestedToDate(cellData?.data), "#,##0")
                )
              : "";
          }}
        />
        <Column
          dataField="vestingPercentage"
          allowSorting={false}
          allowEditing={false}
          minWidth={100}
          alignment={"center"}
          format="percent"
          headerCellRender={() =>
            customHeader(`${psu?.externalCode}-vesting-percentage`, "Vesting", "Percentage")
          }
          cellRender={(cellData) => {
            return cellData.data.vestingPercentage
              ? cellRenderer(
                  cellData,
                  "vesting-percentage",
                  formatNumber(cellData?.data?.vestingPercentage / 100, "#%")
                )
              : "";
          }}
        />
        <Column
          dataField="vestingDate"
          dataType={"date"}
          allowSorting={false}
          allowEditing={false}
          minWidth={130}
          alignment={"center"}
          headerCellRender={() =>
            customHeader(`${psu?.externalCode}-vesting-schedule`, "Vesting", "Schedule")
          }
          cellRender={(cellData) => {
            return cellData.data.vestingDate
              ? cellRenderer(
                  cellData,
                  "vesting-schedule",
                  formatDate(new Date(cellData?.data?.vestingDate), "MMM dd, yyyy")
                )
              : "";
          }}
        />
        <Column
          dataField={"totalAmount"}
          allowSorting={false}
          allowEditing={false}
          alignment={"center"}
          minWidth={100}
          headerCellRender={() =>
            customHeader(`${psu?.externalCode}-total-value`, "Total", "Value")
          }
          cellRender={(cellData) => {
            return calculateTotalValue(cellData?.data, psu?.price ?? 0)
              ? cellRenderer(
                  cellData,
                  "total-amount",
                  formatNumber(calculateTotalValue(cellData?.data, psu?.price ?? 0), "currency")
                    .replaceAll("(", "")
                    .replaceAll(")", "")
                )
              : "";
          }}
        />
      </DataGrid>
    </>
  );
}

export default Grid;
